/** @format */

import { AccountType, CreateAccountStateEnum } from '@shared/data-access/enums';
import { Customer } from '@shared/data-access/interfaces';
import {
  CookieService,
  HttpService,
  Store,
  UtilityService,
  Logger,
} from '@shared/data-access/services';
import { GUEST_USER_ID } from './constants/auth-cookies.constants';
import { HttpHeaders } from '@angular/common/http';
import { Injectable, NgZone, PLATFORM_ID, inject, signal } from '@angular/core';
import { Observable, asyncScheduler, firstValueFrom, from, of, race, timer } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  map,
  observeOn,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import { AuthClaimsEnum } from './enums/auth-claims.enum';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  /*
   * Public
   */

  /**
   * @description
   * Authentication status. Updated via "getIsAuthenticated"
   */
  public isAuthenticated = false;

  /**
   * @description
   * Authentication token. Updated via "getTokenSilently"
   */
  public token: string | null = null;

  /*
   * Private
   */
  private readonly _isAuthenticatedSignal = signal(false);
  private readonly cookieService = inject(CookieService);
  private readonly httpService = inject(HttpService);
  private readonly ngZone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly utilityService = inject(UtilityService);
  private readonly logger = inject(Logger);
  public readonly isAuthenticatedSignal = this._isAuthenticatedSignal.asReadonly();

  private authCheckCache$: Observable<boolean> | undefined | null;

  constructor() {
    /*
     * NOTE: Only perform in the browser!
     */
    /* istanbul ignore else */
    if (isPlatformBrowser(this.platformId)) {
      // Subscribe to the CUSTOMER state so that the associated cookie(s) can be updated automatically
      this.store.select('CUSTOMER').subscribe((customer) => {
        // Check to see if defined
        if (customer !== undefined) {
          // Set the auth cookies
          this.setAuthCookies(customer);
        }
      });
    }
  }

  /**
   * @description
   * Checks to see if there is already a cookie / state available and will use that data if available
   */
  public checkIfReturningCustomer(): void {
    // Create a temp Customer
    const tempCustomer: Customer = new Customer();

    /*
     * Use the NGX auth cookie as the primary data, and override from the legacy cookie where necessary
     */
    // Check to see if NGX auth cookie is present
    /* istanbul ignore else */

    if (
      this.cookieService.check('DCSG_NGX_CUST') &&
      this.cookieService.get('DCSG_NGX_CUST').trim() !== ''
    ) {
      try {
        // Set the Customer
        const ngxCustomerCookie = JSON.parse(this.cookieService.get('DCSG_NGX_CUST'));

        // Set the properties from the cookie
        tempCustomer.accountType = ngxCustomerCookie.accountType;
        tempCustomer.accountStatus = ngxCustomerCookie.accountStatus;
        tempCustomer.addressId = ngxCustomerCookie.addressId;
        tempCustomer.addressLine = ngxCustomerCookie.addressLine;
        tempCustomer.addressType = ngxCustomerCookie.addressType;
        tempCustomer.city = ngxCustomerCookie.city;
        tempCustomer.country = ngxCustomerCookie.country;
        tempCustomer.email1 = ngxCustomerCookie.email1;
        tempCustomer.firstName = ngxCustomerCookie.firstName;
        tempCustomer.lastName = ngxCustomerCookie.lastName;
        tempCustomer.lastUpdate = ngxCustomerCookie.lastUpdate;
        tempCustomer.logonId = ngxCustomerCookie.logonId;
        tempCustomer.nickName = ngxCustomerCookie.nickName;
        tempCustomer.passwordExpired = ngxCustomerCookie.passwordExpired;
        tempCustomer.phone1 = ngxCustomerCookie.phone1;
        tempCustomer.preferredCurrency = ngxCustomerCookie.preferredCurrency;
        tempCustomer.primary = ngxCustomerCookie.primary;
        tempCustomer.profileType = ngxCustomerCookie.profileType;
        tempCustomer.receiveEmailPreference = ngxCustomerCookie.receiveEmailPreference;
        tempCustomer.receiveSMSNotification = ngxCustomerCookie.receiveSMSNotification;
        tempCustomer.receiveSMSPreference = ngxCustomerCookie.receiveSMSPreference;
        tempCustomer.registrationApprovalStatus = ngxCustomerCookie.registrationApprovalStatus;
        tempCustomer.registrationDateTime = ngxCustomerCookie.registrationDateTime;
        tempCustomer.sessionId = ngxCustomerCookie.sessionId || this.utilityService.generateUUID();
        tempCustomer.state = ngxCustomerCookie.state;
        tempCustomer.userId = ngxCustomerCookie.userId || GUEST_USER_ID;
        tempCustomer.x_userLastSession = ngxCustomerCookie.x_userLastSession;
        tempCustomer.zipCode = ngxCustomerCookie.zipCode;
        tempCustomer.visitCount = ngxCustomerCookie.visitCount;
        tempCustomer.expires = ngxCustomerCookie.expires;
      } catch {
        this.logger.warn('Unable to parse DCSG_NGX_CUST cookie. Falling back to guest customer.');
      }
    }

    // Set the account type
    tempCustomer.accountType = tempCustomer.accountType || AccountType.GUEST;

    // Set the expires if necessary
    tempCustomer.expires =
      tempCustomer.expires || this.utilityService.addDaysToDate(new Date(), 30);

    // Update the visit count
    tempCustomer.visitCount =
      tempCustomer.visitCount && tempCustomer.visitCount > 0 ? tempCustomer.visitCount + 1 : 1;

    // Update the Store
    this.store.set('CUSTOMER', tempCustomer);
  }

  /**
   * @description
   * Returns observable with authentication value and updates the
   * isAuthenticated property on the service if the user is already authenticated
   */
  public getIsAuthenticated$(): Observable<boolean> {
    const authFunctions = globalThis.authFunctions;
    if (authFunctions?.isAuthenticated) {
      return from(this.ngZone.runOutsideAngular(() => authFunctions.isAuthenticated())).pipe(
        observeOn(asyncScheduler),
        tap((result: boolean) => {
          this.ngZone.run(() => {
            this.isAuthenticated = result;
            this._isAuthenticatedSignal.set(result);
          });
        }),
      );
    } else {
      return of(false);
    }
  }

  /**
   * Returns an Observable that emits the user's authentication status once the `globalThis.authFunctions.isAuthenticated` method becomes available.
   *
   * This method uses a hybrid strategy combining efficient polling, soft timeout fallback, caching, and Angular zone optimization:
   *
   * - Platform check: Immediately returns `false` if the code is not running in a browser environment (e.g., during server-side rendering).
   * - Polling outside Angular zone: Uses `NgZone.runOutsideAngular()` to run timers for polling and timeout outside Angular's zone, preventing unnecessary change detection cycles.
   * - Soft timeout fallback: If `authFunctions.isAuthenticated` is not available within 1000ms, the observable emits `false` as a fallback without throwing an error.
   * - Execution outside Angular zone: The actual call to `isAuthenticated()` is executed outside Angular's zone for performance.
   * - State update inside Angular zone: The result is passed back into Angular's zone to update the internal `isAuthenticated` property.
   * - Caching: The result is cached using `authCheckCache$` to avoid redundant polling and execution during the same lifecycle.
   * - Cleanup: The cache is cleared after the observable completes using `finalize()`, allowing future re-evaluation.
   * - Replay: The observable is shared and replayed using `shareReplay(1)` so multiple subscribers receive the same result without re-triggering the logic.
   *
   * @returns Observable<boolean> - Emits `true` or `false` based on the user's authentication status or fallback.
   */
  public getIsAuthenticatedSafely$(): Observable<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(false);
    }

    if (this.authCheckCache$) {
      return this.authCheckCache$;
    }

    const checkIntervalMs = 100;
    const timeoutMs = 1000;

    const waitForAuthFunction$ = this.ngZone.runOutsideAngular(() =>
      timer(0, checkIntervalMs).pipe(
        map(() => globalThis.authFunctions?.isAuthenticated),
        filter(
          (isAuthenticatedFn): isAuthenticatedFn is NonNullable<typeof isAuthenticatedFn> =>
            typeof isAuthenticatedFn === 'function',
        ),
        take(1),
      ),
    );

    const timeoutFallback$ = this.ngZone.runOutsideAngular(() =>
      timer(timeoutMs).pipe(map(() => false as const)),
    );

    this.authCheckCache$ = race(waitForAuthFunction$, timeoutFallback$).pipe(
      switchMap((isAuthenticatedFn) => {
        if (isAuthenticatedFn === false) {
          this.logger.warn(
            'authFunctions.isAuthenticated not available within timeout. Using fallback.',
          );
          return of(false);
        }
        return from(this.ngZone.runOutsideAngular(() => isAuthenticatedFn()));
      }),
      observeOn(asyncScheduler),
      tap((result: boolean) => {
        this.ngZone.run(() => {
          this.isAuthenticated = result;
          this._isAuthenticatedSignal.set(result);
        });
      }),
      finalize(() => {
        this.authCheckCache$ = null;
      }),
      shareReplay(1),
    );

    return this.authCheckCache$;
  }

  /**
   * @description
   * Returns observable with fresh token value and updates the token
   * property on the service
   */
  public getTokenSilently$(): Observable<string> {
    const authFunctions = globalThis.authFunctions;
    // Should be defined if this is run after `getIsAuthenticated$` returns true, but just in case...
    if (!authFunctions?.getTokens) {
      return of('');
    }
    const tokens$ = from(authFunctions.getTokens());

    // Return the completed stream from the Promise
    return tokens$.pipe(
      map((token: string) => JSON.parse(token).accessToken),
      catchError(() =>
        tokens$.pipe(
          map(
            (token: string) =>
              `${token && token?.indexOf('Bearer ') === -1 ? 'Bearer ' : ''}${token}`,
          ),
        ),
      ),
      tap((token: string) => (this.token = token)),
    );
  }

  /**
   * @description
   * Handles authentication on app entry. Place in app.component.ts
   * NOTE: This should only be called on app initialization and
   * sets up local authentication streams
   */
  public localAuthSetup(): void {
    this.getIsAuthenticated$().subscribe();
  }

  /**
   * @description
   * This will redirect athelte to create account page if athelete not a existing athelete or
   * it will take to the sign in page.
   * A desired redirect path can be passed to createAccount method (e.g., from a route guard).
   */

  public createAccount(appState?: { target: string }): Observable<null> {
    const route = this.router.url.toLowerCase();
    const isCartOrCheckout =
      route.includes('orderitemdisplay') || route.includes('singlepagecheckout');

    // if create account exists do that action
    /* istanbul ignore next */
    if (globalThis.authFunctions?.createAccount) {
      globalThis.authFunctions.createAccount(
        appState?.target ? appState.target : undefined,
        isCartOrCheckout,
      );
    }
    return of(null);
  }

  /**
   * @description
   * A desired redirect path can be passed to login method (e.g., from a route guard).
   */
  public login(appState?: { target?: string }, skipCompleteProfile = false): Observable<null> {
    const route = this.router.url.toLowerCase();
    const isCartOrCheckout =
      route.includes('orderitemdisplay') || route.includes('singlepagecheckout');

    if (globalThis.authFunctions?.login) {
      globalThis.authFunctions.login(
        appState?.target ? appState.target : undefined,
        isCartOrCheckout || skipCompleteProfile,
      );
    }
    return of(null);
  }

  /**
   * @description
   * Triggers Auth0 logout sequence. Will redirect to the url provided in
   * the IAuth0ClientOptions object
   */
  public logout(): void {
    globalThis.authFunctions?.logout();
  }

  public createAccount$(): Observable<CreateAccountStateEnum> {
    const headers = new HttpHeaders({
      'X-TIMEOUT': '15000',
    });

    return this.httpService
      .post<{ status: number }>('/api/v1/accounts', {}, { observe: 'response', headers })
      .pipe(
        map((response) => {
          if (response.status === 204) {
            return CreateAccountStateEnum.SUCCESS;
          }

          return CreateAccountStateEnum.ERROR;
        }),
        catchError((err) => {
          let result = CreateAccountStateEnum.ERROR;
          if (err.status === 406) {
            result = CreateAccountStateEnum.INVALID_EMAIL;
          }

          return of(result);
        }),
      );
  }

  /**
   * @description
   * returns identityId from token
   */
  public async getScoreCardNumber$(): Promise<string> {
    return await firstValueFrom(this.getTokenSilently$())
      .then((token: string) => {
        return JSON.parse(atob(token.split('.')[1]))[AuthClaimsEnum.SCORECARD]; // get scorecard number from auth JWT
      })
      .catch((err) => {
        throw err;
      });
  }

  /**
   * @description
   * returns identityId from token
   */
  public async getIdentityId$(): Promise<string> {
    return await firstValueFrom(this.getTokenSilently$())
      .then((token: string) => {
        return JSON.parse(atob(token.split('.')[1]))[AuthClaimsEnum.IDENTITYID];
      })
      .catch((err) => {
        throw err;
      });
  }

  /**
   * @description
   * triggers reset password flow
   */
  public triggerResetPassword$(): Observable<null> {
    return new Observable<null>((observer) => {
      globalThis.authFunctions
        ?.triggerResetPassword()
        .then(() => {
          observer.next(null);
          observer.complete();
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  }

  /**
   * @description
   * Sets the NGX auth cookie
   */
  private setAuthCookies(customer: Customer): void {
    // Set the NGX auth cookie
    this.cookieService.set('DCSG_NGX_CUST', JSON.stringify(customer), customer.expires || 30, '/');
  }
}
